import type { WebStudioScope } from "../api/boot";
import { WEB_STUDIO_CLOUD_API_OPERATION_CONTRACT_VERSION } from "../api/generatedClient";

const STORAGE_KEY = "jacqos.web_studio.scope_identity";
const CHANNEL_NAME = "jacqos.web_studio.session";
const FORBIDDEN_DURABLE_CREDENTIAL_KEY = /(^|\.|_)session_token$|bearer|raw_token/i;

export interface BrowserStateSnapshot {
  identity: string;
  protectedStateCleared: boolean;
}

export function currentScopeIdentity(
  scope: WebStudioScope,
  routeAvailabilityDigest?: string,
): string {
  return [
    WEB_STUDIO_CLOUD_API_OPERATION_CONTRACT_VERSION,
    routeAvailabilityDigest ?? "route-availability-unknown",
    scope.accountId,
    scope.organizationId,
    scope.projectId,
    scope.workspaceId,
    scope.environmentId,
    scope.worldviewId ?? "",
    scope.lineageId ?? "",
    scope.activationId ?? "",
  ].join("|");
}

export function reconcileBrowserState(
  scope: WebStudioScope,
  routeAvailabilityDigest?: string,
): BrowserStateSnapshot {
  const identity = currentScopeIdentity(scope, routeAvailabilityDigest);
  const previous = window.localStorage.getItem(STORAGE_KEY);
  const changed = previous !== null && previous !== identity;
  if (changed) {
    clearProtectedBrowserState();
  }
  window.localStorage.setItem(STORAGE_KEY, identity);
  return { identity, protectedStateCleared: changed };
}

export function installBrowserStateBroadcast(onRefreshRequired: () => void): () => void {
  if (!("BroadcastChannel" in window)) {
    return () => undefined;
  }
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event) => {
    if (event.data === "web-studio-refresh-required") {
      clearProtectedBrowserState();
      onRefreshRequired();
    }
  };
  return () => channel.close();
}

export function publishBrowserStateRefresh(): void {
  if (!("BroadcastChannel" in window)) {
    return;
  }
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage("web-studio-refresh-required");
  channel.close();
}

/**
 * Public entry point for account-session transitions: entering the sign-in
 * gate (signed out, expired, revoked) and confirmed sign-out clear protected
 * per-session browser state through the same accepted clearing behavior the
 * scope-identity reconciliation uses.
 */
export function clearProtectedWebStudioBrowserState(): void {
  clearProtectedBrowserState();
}

function clearProtectedBrowserState(): void {
  const protectedKeys: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (
      key?.startsWith("jacqos.web_studio.protected.") ||
      (key?.startsWith("jacqos.web_studio.") && FORBIDDEN_DURABLE_CREDENTIAL_KEY.test(key))
    ) {
      protectedKeys.push(key);
    }
  }
  for (const key of protectedKeys) {
    window.localStorage.removeItem(key);
  }
  if ("sessionStorage" in window) {
    const sessionKeys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (
        key?.startsWith("jacqos.web_studio.protected.") ||
        (key?.startsWith("jacqos.web_studio.") && FORBIDDEN_DURABLE_CREDENTIAL_KEY.test(key))
      ) {
        sessionKeys.push(key);
      }
    }
    for (const key of sessionKeys) {
      window.sessionStorage.removeItem(key);
    }
  }
}

export function assertNoDurableBearerTokenStorage(): void {
  const stores: Storage[] = [window.localStorage];
  if ("sessionStorage" in window) {
    stores.push(window.sessionStorage);
  }
  const forbiddenKeys: string[] = [];
  for (const store of stores) {
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key?.startsWith("jacqos.web_studio.") && FORBIDDEN_DURABLE_CREDENTIAL_KEY.test(key)) {
        forbiddenKeys.push(key);
      }
    }
  }
  if (forbiddenKeys.length > 0) {
    throw new Error(`Forbidden durable Web Studio credential keys: ${forbiddenKeys.join(", ")}`);
  }
}
